import { useEffect, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth/AuthProvider.js';
import { toUserMessage } from '../lib/errors.js';

// Bottone "Continua con Google". Si mostra solo se il plugin nativo
// @capacitor/browser è disponibile (APK aggiornato) o su web: così l'OTA su
// un APK vecchio senza il plugin non espone un bottone che andrebbe in crash.
export function GoogleSignInButton({ disabled = false }: { disabled?: boolean }): JSX.Element | null {
  const { t } = useTranslation();
  const { signInWithGoogle } = useAuth();
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        // Su web/PWA il flow OAuth Google usa un redirect full-page e NON
        // dipende dal plugin nativo @capacitor/browser — il bottone va
        // sempre mostrato. Su nativo (APK) serve invece il plugin per
        // aprire la Custom Tab di sistema; se l'APK è vecchio e non lo ha
        // a bordo, nascondiamo per evitare crash.
        if (!Capacitor.isNativePlatform()) {
          setAvailable(true);
          return;
        }
        setAvailable(Capacitor.isPluginAvailable('Browser'));
      } catch {
        setAvailable(false);
      }
    })();
  }, []);

  if (!available) return null;

  async function onClick(): Promise<void> {
    if (disabled) return;
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
      // Su nativo la Custom Tab prende il controllo; il ritorno è gestito dal
      // DeepLinkHandler. Lo spinner resta finché la tab non si apre.
    } catch (err: unknown) {
      setError(toUserMessage(err));
      setLoading(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0', color: '#94a3b8', fontSize: 12 }}>
        <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
        oppure
        <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
      </div>
      <IonButton
        expand="block"
        fill="outline"
        disabled={loading || disabled}
        onClick={onClick}
        style={{
          '--border-radius': '12px',
          '--border-color': '#cbd5e1',
          '--color': '#0f172a',
          height: 48,
        } as React.CSSProperties}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.88 2.68-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95L3.97 7.28C4.68 5.16 6.66 3.58 9 3.58z" />
          </svg>
          {loading ? t('common.loading') : t('auth.continueGoogle')}
        </span>
      </IonButton>
      {error && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginTop: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}
